import {
   AppointmentPractitioner,
   AvailabilitySimple,
   AvailabilitySimpleQuery,
   HealthcareProviderLocationIdentifier,
   PractitionerAbleToCompleteAppointmentQuery,
} from '@dreryk/egabinet-appointment-frontend-api';
import {
   HealthcareProviderLocation,
   HealthcareProviderLocationQuery,
} from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { CurrentHealthcareProviderPaymentsConfigurationEnabledQuery } from '@dreryk/egabinet-online-payments-integration-frontend-api';

import { isDemo } from '@dreryk/demo-variant-frontend';
import { useCqrsFind, useCqrsSearch } from '@dreryk/command-query-react-common';
import { TimetableSlotSelectionEvent } from '@dreryk/react-calendar';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Range } from '@dreryk/watermelon';

import { appointmentsTranslationKeys } from '../../../../translations';

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const allEqual = <T extends unknown>(array: T[]): boolean => {
   const jsonStrings = array.map((element) => JSON.stringify(element));
   return new Set(jsonStrings).size === 1;
};

const getHealthcareProviderLocationIdentifierFromScheduleIfSingle = (
   availabilities: AvailabilitySimple[]
): HealthcareProviderLocationIdentifier | null => {
   const locations = availabilities
      .map((availability) => availability.location)
      .filter((value): value is HealthcareProviderLocationIdentifier => Boolean(value));

   if (!locations?.length) {
      return null;
   }

   if (allEqual(locations)) {
      return locations[0];
   }

   return null;
};

type AppointmentCreateFormLoaderRenderer = (result: {
   practitioners: AppointmentPractitioner[];
   healthcareProviderLocations: HealthcareProviderLocation[];
   healthcareProviderLocationIdentifierFromSchedule: HealthcareProviderLocationIdentifier | null;
   onlinePaymentsIntegrationExists: boolean;
}) => JSX.Element;

type AppointmentCreateFormLoaderProps = {
   readonly selectedSlot: TimetableSlotSelectionEvent<AppointmentPractitioner>;
   readonly children: AppointmentCreateFormLoaderRenderer;
};

export const AppointmentCreateFormLoader = (props: AppointmentCreateFormLoaderProps): JSX.Element => {
   const {
      data: practitioners,
      loading: practitionersLoading,
      error: practitionersError,
   } = useCqrsSearch<AppointmentPractitioner>(() => new PractitionerAbleToCompleteAppointmentQuery());

   const {
      data: healthcareProviderLocations,
      loading: healthcareProviderLocationsLoading,
      error: healthcareProviderLocationsError,
   } = useCqrsSearch<HealthcareProviderLocation>(() => new HealthcareProviderLocationQuery());

   const {
      data: availabilities,
      loading: availabilitiesLoading,
      error: availabilitiesError,
   } = useCqrsSearch<AvailabilitySimple>(() => {
      const query = new AvailabilitySimpleQuery();
      query.dateRange = Range.closedOpen(props.selectedSlot.start, props.selectedSlot.end);
      query.practitionerIds = [props.selectedSlot.resource.id];
      return query;
   });

   const {
      data: onlinePaymentsIntegrationExists,
      loading: onlinePaymentsIntegrationExistsLoading,
      error: onlinePaymentsIntegrationExistsError,
   } = useCqrsFind(() => new CurrentHealthcareProviderPaymentsConfigurationEnabledQuery(), { enabled: !isDemo() });

   if (
      practitionersLoading ||
      healthcareProviderLocationsLoading ||
      availabilitiesLoading ||
      onlinePaymentsIntegrationExistsLoading
   ) {
      return <LoaderSpinner />;
   }

   if (
      practitionersError ||
      healthcareProviderLocationsError ||
      availabilitiesError ||
      onlinePaymentsIntegrationExistsError
   ) {
      return (
         <Message
            testId={'practitionersAppointmentFetchError'}
            type={MessageType.NEGATIVE}
            label={appointmentsTranslationKeys.create.form.errors.fetchData}
            displayIndicator
         />
      );
   }

   return props.children({
      practitioners: practitioners,
      healthcareProviderLocations: healthcareProviderLocations,
      healthcareProviderLocationIdentifierFromSchedule: getHealthcareProviderLocationIdentifierFromScheduleIfSingle(
         availabilities
      ),
      onlinePaymentsIntegrationExists: Boolean(onlinePaymentsIntegrationExists) ?? false,
   });
};
